<template>
  <v-dialog
    v-if="featureEnabled"
    :value="open"
    persistent
    width="520"
    @keydown.esc="handleDismiss"
  >
    <v-card class="djfld-card" data-testid="delete-job-family-level-dialog">
      <div class="djfld-header">
        <span class="djfld-featured-icon" data-testid="djfld-featured-icon">
          <svg
            class="djfld-trash-icon"
            width="24"
            height="24"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M13.3333 4.99935V4.33268C13.3333 3.39926 13.3333 2.93255 13.1517 2.57603C12.9919 2.26243 12.7369 2.00746 12.4233 1.84767C12.0668 1.66602 11.6001 1.66602 10.6667 1.66602H9.33333C8.39991 1.66602 7.9332 1.66602 7.57668 1.84767C7.26308 2.00746 7.00811 2.26243 6.84832 2.57603C6.66667 2.93255 6.66667 3.39926 6.66667 4.33268V4.99935M8.33333 9.58268V13.7493M11.6667 9.58268V13.7493M2.5 4.99935H17.5M15.8333 4.99935V14.3327C15.8333 15.7328 15.8333 16.4329 15.5608 16.9677C15.3212 17.4381 14.9387 17.8205 14.4683 18.0602C13.9335 18.3327 13.2335 18.3327 11.8333 18.3327H8.16667C6.76654 18.3327 6.06647 18.3327 5.53169 18.0602C5.06129 17.8205 4.67883 17.4381 4.43915 16.9677C4.16667 16.4329 4.16667 15.7328 4.16667 14.3327V4.99935"
              stroke="currentColor"
              stroke-width="1.66667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <button
          type="button"
          class="djfld-close"
          aria-label="Close Delete Level dialog"
          data-testid="djfld-close"
          :disabled="busy"
          @click="handleDismiss"
        >
          <svg
            class="djfld-close__icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M18 6 6 18M6 6l12 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <h2 id="djfld-title" class="djfld-title" data-testid="djfld-title">{{ title }}</h2>
        <p class="djfld-supporting-text" data-testid="djfld-supporting-text">
          {{ supportingText }}
        </p>
      </div>

      <div v-if="mode === 'reassign'" class="djfld-content">
        <label class="djfld-label" for="djfld-level-select" data-testid="djfld-select-label">
          Move positions to<span class="djfld-required" aria-hidden="true">*</span>
        </label>
        <button
          id="djfld-level-select"
          type="button"
          class="djfld-select"
          :class="{ 'djfld-select--open': pickerOpen }"
          aria-haspopup="listbox"
          :aria-expanded="pickerOpen ? 'true' : 'false'"
          :disabled="busy"
          data-testid="djfld-select"
          @click="togglePicker"
        >
          <span
            class="djfld-select__value"
            :class="{ 'djfld-select__value--placeholder': !selectedTarget }"
          >
            {{ selectedTarget ? formatOrdinalName(selectedTarget.ordinal, selectedTarget.name) : "Select a level" }}
          </span>
          <svg
            class="djfld-chevron"
            :class="{ 'djfld-chevron--open': pickerOpen }"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              stroke-width="1.66667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <ul
          v-if="pickerOpen"
          class="djfld-menu"
          role="listbox"
          data-testid="djfld-menu"
          @keydown.esc.stop="closePicker"
        >
          <li
            v-for="option in siblingOptions"
            :key="option.id"
            class="djfld-menu-item"
            role="option"
            tabindex="0"
            :aria-selected="selectedTargetId === option.id ? 'true' : 'false'"
            data-testid="djfld-menu-item"
            @click="selectTarget(option)"
            @keydown.enter.prevent="selectTarget(option)"
            @keydown.space.prevent="selectTarget(option)"
          >
            <span class="djfld-menu-item__label">{{ formatOrdinalName(option.ordinal, option.name) }}</span>
            <span class="djfld-menu-item__descriptor">{{ option.descriptor }}</span>
          </li>
        </ul>

        <p class="djfld-caption" data-testid="djfld-caption">{{ caption }}</p>
      </div>

      <p v-if="errorText" class="djfld-error" role="alert" data-testid="djfld-error">
        {{ errorText }}
      </p>

      <div class="djfld-actions">
        <button
          type="button"
          class="djfld-btn djfld-btn--secondary"
          data-testid="djfld-cancel"
          :disabled="busy"
          @click="handleDismiss"
        >
          Cancel
        </button>
        <button
          type="button"
          class="djfld-btn"
          :class="deleteButtonClass"
          :disabled="deleteDisabled"
          :aria-busy="busy ? 'true' : 'false'"
          data-testid="djfld-confirm"
          @click="handleConfirm"
        >
          <span v-if="busy" class="djfld-spinner" aria-hidden="true" data-testid="djfld-spinner" />
          <span>{{ confirmLabel }}</span>
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "DeleteJobFamilyLevelDialog",
};
</script>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";

import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import {
  buildSiblingOptions,
  formatOrdinalName,
  noPositionsSupportingText,
  onlyLevelSupportingText,
  pickerExclusionCaption,
  reassignSupportingText,
  resolveErrorMessage,
  resolveMode,
  targetSelectedHelper,
} from "./deleteJobFamilyLevelDialogHelpers";

/**
 * ER-11643 — DeleteJobFamilyLevelDialog (Organization > Job Families > Job Family
 * detail > Levels table row "⋯" menu > Delete).
 *
 * Levels have no status (Job Family §10.4) — Delete is always available and is the
 * only removal path. Five Situation variants, resolved from `level.positionCount`
 * and `siblingLevels.length`:
 * - Only level (no other Levels in the Family): blocked, no destructive action, the
 *   primary button reads "Close".
 * - No positions (siblings exist, nothing on this Level): straight confirm.
 * - Needs reassignment / Picker open / Target selected (siblings exist, Positions on
 *   this Level): a level picker scoped to the same Family, excluding this Level BY
 *   ID (names are not unique within a Family). Delete stays disabled until a target
 *   is committed.
 *
 * Controlled, presentational: the parent owns `open`, supplies `level` and the
 * caller-filtered `siblingLevels`, and receives `confirm` ({ levelId, reassignTo })
 * or `cancel`. No API call inside the component — the parent performs the DELETE
 * and can feed a server error code back through the `error` prop
 * (JOB_FAMILY_LEVEL_CANNOT_DELETE_LAST / _REASSIGN_TARGET_REQUIRED / _INVALID) so it
 * is surfaced here instead of failing silently.
 *
 * Shipped behind the linked idea flag (idea-MDP-581, "Job Family — Discipline &
 * Career-Ladder Grouping for Positions") — the same kill switch that gates every
 * other Job Family component. Renders by default; set the flag to false to keep the
 * dialog closed until a screen wires it.
 */
const props = defineProps({
  /** Controls dialog visibility (v-model:open). */
  open: {
    type: Boolean,
    default: false,
  },
  /** The Level being deleted: { id, ordinal, name, positionCount }. */
  level: {
    type: Object,
    required: true,
  },
  /** The other Levels in the same Family, caller-filtered: { id, ordinal, name, descriptor }[]. */
  siblingLevels: {
    type: Array,
    default: () => [],
  },
  /** True while the delete/reassign request is in flight. */
  busy: {
    type: Boolean,
    default: false,
  },
  /** A JOB_FAMILY_LEVEL_* server error code (or message) to surface, if any. */
  error: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["confirm", "cancel"]);

const featureEnabled = ref(false);
const proxy = getCurrentInstance()?.proxy;

const pickerOpen = ref(false);
const selectedTargetId = ref(null);

const mode = computed(() => resolveMode(props.level, props.siblingLevels));
const siblingOptions = computed(() => buildSiblingOptions(props.level, props.siblingLevels));
const selectedTarget = computed(
  () => siblingOptions.value.find((option) => option.id === selectedTargetId.value) || null
);

const errorText = computed(() => resolveErrorMessage(props.error));

const title = computed(() => (mode.value === "onlyLevel" ? "Can\u2019t delete this level" : "Delete level"));

const supportingText = computed(() => {
  if (mode.value === "onlyLevel") return onlyLevelSupportingText(props.level);
  if (mode.value === "noPositions") return noPositionsSupportingText(props.level);
  return reassignSupportingText(props.level);
});

const caption = computed(() =>
  selectedTarget.value
    ? targetSelectedHelper(props.level, selectedTarget.value)
    : pickerExclusionCaption(props.level)
);

const confirmLabel = computed(() => {
  if (props.busy) return "Deleting";
  return mode.value === "onlyLevel" ? "Close" : "Delete";
});

const deleteDisabled = computed(() => {
  if (props.busy) return true;
  if (mode.value === "reassign") return !selectedTarget.value;
  return false;
});

const deleteButtonClass = computed(() => {
  if (mode.value === "onlyLevel") return "djfld-btn--neutral";
  return deleteDisabled.value ? "djfld-btn--disabled" : "djfld-btn--destructive";
});

function togglePicker() {
  if (props.busy) return;
  pickerOpen.value = !pickerOpen.value;
}

function closePicker() {
  pickerOpen.value = false;
}

function selectTarget(option) {
  selectedTargetId.value = option.id;
  pickerOpen.value = false;
}

function handleConfirm() {
  if (mode.value === "onlyLevel") {
    emit("cancel");
    return;
  }
  if (deleteDisabled.value) return;
  emit("confirm", {
    levelId: props.level?.id,
    reassignTo: selectedTarget.value ? selectedTarget.value.id : null,
  });
}

function handleDismiss() {
  if (props.busy) return;
  emit("cancel");
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      pickerOpen.value = false;
      selectedTargetId.value = null;
    }
  }
);

watch(
  () => props.level?.id,
  () => {
    pickerOpen.value = false;
    selectedTargetId.value = null;
  }
);

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dialog shut.
    featureEnabled.value = true;
  }
});
</script>

<style scoped>
.djfld-card {
  position: relative;
  background-color: #ffffff;
  /* !important overrides Vuetify's v-card default radius/elevation classes. */
  border-radius: 12px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
}

.djfld-header {
  padding: 24px 24px 0;
}

.djfld-featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  background-color: #fef3f2;
  border: 1px solid #fee4e2;
  border-radius: 12px;
  color: #d92d20;
}

.djfld-trash-icon {
  display: block;
}

.djfld-close {
  position: absolute;
  top: 24px;
  right: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #667085;
  cursor: pointer;
}

.djfld-close:hover:not(:disabled) {
  background: #f2f4f7;
}

.djfld-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.djfld-close:focus-visible,
.djfld-select:focus-visible,
.djfld-btn:focus-visible,
.djfld-menu-item:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.djfld-title {
  margin: 0 0 4px;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.djfld-supporting-text {
  margin: 0;
  color: #667085;
  font-size: 14px;
  line-height: 20px;
}

.djfld-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px 24px 0;
}

.djfld-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.djfld-required {
  color: #f04438;
}

.djfld-select {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 42px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  color: #667085;
  cursor: pointer;
}

.djfld-select--open {
  border-color: #d6bbfb;
}

.djfld-select:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.djfld-select__value {
  overflow: hidden;
  color: #101828;
  font-size: 16px;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.djfld-select__value--placeholder {
  color: #667085;
}

.djfld-chevron {
  flex-shrink: 0;
  transition: transform 0.15s ease;
}

.djfld-chevron--open {
  transform: rotate(180deg);
}

.djfld-menu {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}

.djfld-menu-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
}

.djfld-menu-item:hover,
.djfld-menu-item[aria-selected="true"] {
  background: #f9fafb;
}

.djfld-menu-item__label {
  color: #101828;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.djfld-menu-item__descriptor {
  color: #667085;
  font-size: 13px;
  line-height: 18px;
}

.djfld-caption {
  margin: 0;
  color: #667085;
  font-size: 13px;
  line-height: 18px;
}

.djfld-error {
  margin: 6px 24px 0;
  color: #d92d20;
  font-size: 13px;
  line-height: 18px;
}

.djfld-actions {
  display: flex;
  gap: 12px;
  padding: 32px 24px 24px;
}

.djfld-btn {
  box-sizing: border-box;
  display: inline-flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.djfld-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

.djfld-btn--secondary:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.djfld-btn--destructive {
  background: #d92d20;
  border: 1px solid #d92d20;
  color: #ffffff;
}

.djfld-btn--disabled,
.djfld-btn--destructive:disabled {
  background: #f2f4f7;
  border: 1px solid #eaecf0;
  color: #98a2b3;
  cursor: not-allowed;
}

.djfld-btn--neutral {
  background: #f2f4f7;
  border: 1px solid #eaecf0;
  color: #344054;
}

.djfld-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: djfld-spin 0.6s linear infinite;
}

.djfld-btn--neutral .djfld-spinner {
  border: 2px solid rgba(52, 64, 84, 0.3);
  border-top-color: #344054;
}

@keyframes djfld-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
